import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Box, Button, LinearProgress, Typography } from '@mui/material';

import {
  useCrmResyncOpportunity,
  useCrmResyncProgressPercent,
  useGetCrmResyncStatus,
} from '~/hooks/apis/offers/useCrmResyncOpportunity';
import { offerQueryKeys } from '~/hooks/apis/offers/useGetOffer';

interface Props {
  opportunityId: string | undefined;
  size?: 'small' | 'medium';
}

export default function CrmResyncButton({ opportunityId, size = 'medium' }: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [requestedAt, setRequestedAt] = useState<string | undefined>(undefined);

  const crmResyncMutation = useCrmResyncOpportunity();
  const { data: resyncStatus, isError: isStatusError } = useGetCrmResyncStatus(opportunityId, requestedAt, {
    enabled: !!requestedAt,
  });

  const isSyncing =
    !!requestedAt && !isStatusError && resyncStatus?.status !== 'complete' && resyncStatus?.status !== 'failed';
  const estimatedPercent = useCrmResyncProgressPercent(requestedAt, isSyncing);

  useEffect(() => {
    if (resyncStatus?.status === 'complete') {
      // Everything the CM Checklist reads (offer, opportunity, related deals/surcharges/
      // inclusions/blackout dates) is refetched now that the sync has actually landed.
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.all });
      enqueueSnackbar('Resync complete.', { variant: 'success' });
    } else if (resyncStatus?.status === 'failed') {
      enqueueSnackbar(`Resync failed${resyncStatus.message ? `: ${resyncStatus.message}` : '.'}`, {
        variant: 'error',
      });
    } else {
      return;
    }

    setRequestedAt(undefined);
    crmResyncMutation.reset();
  }, [resyncStatus?.status, resyncStatus?.message, queryClient, enqueueSnackbar, crmResyncMutation]);

  useEffect(() => {
    if (isStatusError) {
      enqueueSnackbar('Unable to check resync status. The resync may still complete.', { variant: 'error' });
      setRequestedAt(undefined);
    }
  }, [isStatusError, enqueueSnackbar]);

  function handleClick() {
    if (!opportunityId || isSyncing) {
      return;
    }

    crmResyncMutation.mutate(opportunityId, {
      onSuccess: (resyncResult) => setRequestedAt(resyncResult.requestedAt),
      onError: () => enqueueSnackbar('Failed to start resync. Please try again.', { variant: 'error' }),
    });
  }

  if (isSyncing || crmResyncMutation.isPending) {
    return (
      <Box sx={{ minWidth: 160 }}>
        <Typography variant="caption" color="text.secondary">
          Syncing latest Salesforce data…
        </Typography>
        <LinearProgress
          variant="determinate"
          value={crmResyncMutation.isPending ? 0 : estimatedPercent}
          sx={{ borderRadius: 1, height: 6 }}
        />
      </Box>
    );
  }

  return (
    <Button
      disabled={!opportunityId}
      onClick={handleClick}
      size={size}
      sx={size === 'small' ? { fontSize: 12, lineHeight: 1.2, textTransform: 'none' } : undefined}
      variant="outlined"
    >
      Resync now
    </Button>
  );
}
